import { components } from "@/api-schema";
import { TemporaryPermissionStatus } from "@/api-schema-enums";
import { formatApiDateOnlyToLocale } from "@/common/helpers/format-date";
import { DeviceType } from "@/common/hooks/use-device";
import type { ImplementedAccessorColumnDef } from "@/components/data-table/types";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { EmptyValue } from "@/components/ui/empty-value";
import { UserItem, UserItemVariant } from "@/components/user-item/user-item";
import { StatusCell } from "@/modules/ram/temporary/_components/status-cell";
import i18next from "i18next";
import { CheckIcon, XIcon } from "lucide-react";
import { commonColumns } from "./_constants/common-columns";

export type TemporaryPermissionTableType = components["schemas"]["TemporaryPermission"];

export const temporaryPermissionTableDefaultColumns = {
  columnRequestBy: {
    id: "request_by",
    size: 300,
    enableSorting: false,
    header: () => i18next.t("common:RequestBy"),
    visibility: [DeviceType.Mobile, DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => row.request_by,
    cell: ({ row }) => {
      const requestBy = row.original.request_by;
      if (!requestBy) {
        return <EmptyValue />;
      }
      return <UserItem user={requestBy} variant={UserItemVariant.Compact} />;
    },
  } as ImplementedAccessorColumnDef<TemporaryPermissionTableType, TemporaryPermissionTableType["request_by"]>,
  columnRequestFor: {
    id: "request_for",
    size: 300,
    enableSorting: false,
    header: () => i18next.t("common:RequestFor"),
    visibility: [DeviceType.Mobile, DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => row.request_for,
    cell: ({ row }) => {
      const requestFor = row.original.request_for;
      if (!requestFor) {
        return <EmptyValue />;
      }
      return <UserItem user={requestFor} variant={UserItemVariant.Compact} />;
    },
  } as ImplementedAccessorColumnDef<TemporaryPermissionTableType, TemporaryPermissionTableType["request_for"]>,
  columnValidFrom: {
    id: "valid_from",
    size: 150,
    enableSorting: true,
    header: () => i18next.t("common:ValidFrom"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    align: "right",
    accessorFn: (row) => row.valid_from,
    cell: (row) => formatApiDateOnlyToLocale(row.getValue()) ?? <EmptyValue />,
  } as ImplementedAccessorColumnDef<TemporaryPermissionTableType, TemporaryPermissionTableType["valid_from"]>,
  columnValidUntil: {
    id: "valid_until",
    size: 150,
    enableSorting: true,
    header: () => i18next.t("common:ValidUntil"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    align: "right",
    accessorFn: (row) => row.valid_until,
    cell: (row) => formatApiDateOnlyToLocale(row.getValue()) ?? <EmptyValue />,
  } as ImplementedAccessorColumnDef<TemporaryPermissionTableType, TemporaryPermissionTableType["valid_until"]>,
  columnStatus: {
    id: "status",
    size: 100,
    enableSorting: true,
    header: () => i18next.t("common:Status"),
    visibility: [DeviceType.Mobile, DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => row.status ?? TemporaryPermissionStatus.Pending,
    cell: ({ row }) => {
      const status = row.original.status ?? TemporaryPermissionStatus.Pending;
      return <StatusCell status={status} />;
    },
  } as ImplementedAccessorColumnDef<
    TemporaryPermissionTableType,
    TemporaryPermissionTableType["status"] | TemporaryPermissionStatus.Pending
  >,
  columnActions: {
    id: "temporary-permission-manage-actions",
    size: 120,
    enableSorting: false,
    header: () => i18next.t("common:Actions"),
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => ({
      uuid: row.uuid ?? "",
      status: row.status ?? TemporaryPermissionStatus.Pending,
    }),
    cell: ({ row, table }) => {
      const uuid = row.original.uuid;
      const status = row.original.status ?? TemporaryPermissionStatus.Pending;
      const meta = table.options.meta as {
        onApprove?: (uuid: string) => void;
        onReject?: (uuid: string) => void;
      };

      return (
        <div className="flex gap-2">
          <Button
            size={ButtonSize.Sm}
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            disabled={status === TemporaryPermissionStatus.Approved}
            onClick={() => meta?.onApprove?.(uuid)}
            className="h-8 w-8 p-0"
          >
            <CheckIcon className="h-4 w-4" strokeWidth={3} />
          </Button>
          <Button
            size={ButtonSize.Sm}
            variant={ButtonVariant.Filled}
            colorVariant={ButtonColorVariant.Error}
            disabled={status === TemporaryPermissionStatus.Rejected}
            onClick={() => meta?.onReject?.(uuid)}
            className="h-8 w-8 p-0"
          >
            <XIcon className="h-4 w-4" strokeWidth={3} />
          </Button>
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<
    TemporaryPermissionTableType,
    {
      uuid: string;
      status: TemporaryPermissionTableType["status"] | TemporaryPermissionStatus.Pending;
    }
  >,
  columnUuid: commonColumns.columnUuid<TemporaryPermissionTableType>(),
  columnCreatedAt: commonColumns.columnCreatedAt<TemporaryPermissionTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<TemporaryPermissionTableType>(),
};
